import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { Dialog } from "../../components/Dialog";
import { Loading } from "../../components/Loading";
import { useToast } from "../../components/Toast";
import { buildValues, FieldError, initialFields, toText, type FieldState } from "../../lib/adminForm";
import { api, type AdminColumn, type AdminTable, type RowKey } from "../../lib/api";
import { keys, useAdminMutation } from "../../lib/queries";

interface Props {
  table: AdminTable;
  /** null: new row. */
  rowKey: RowKey | null;
  /** New rows start with the active filters, e.g. subscriber_id of the listed watches. */
  prefill?: Record<string, unknown>;
  onClose: () => void;
}

export function RowDialog({ table, rowKey, prefill, onClose }: Props) {
  const row = useQuery({
    queryKey: [...keys.admin, "row", table.name, rowKey],
    queryFn: () => api.admin.row(table.name, rowKey!),
    enabled: rowKey !== null,
    // Always edit the current value, not a cached one.
    gcTime: 0,
  });
  const title = rowKey ? `Modifier ${table.name} ${JSON.stringify(rowKey)}` : `Nouvelle ligne dans ${table.name}`;

  return (
    <Dialog open onClose={onClose} title={title} wide>
      {rowKey === null ? (
        <RowForm table={table} original={null} prefill={prefill} onClose={onClose} />
      ) : row.isError ? (
        <div className="notice notice--error" role="alert">
          {row.error.message}
        </div>
      ) : !row.data ? (
        <Loading />
      ) : (
        <RowForm table={table} original={row.data.values} rowKey={rowKey} onClose={onClose} />
      )}
    </Dialog>
  );
}

function RowForm({
  table,
  original,
  rowKey,
  prefill,
  onClose,
}: {
  table: AdminTable;
  original: Record<string, unknown> | null;
  rowKey?: RowKey;
  prefill?: Record<string, unknown>;
  onClose: () => void;
}) {
  const [fields, setFields] = useState<Record<string, FieldState>>(() => {
    const start = initialFields(table.columns, original);
    for (const [name, value] of Object.entries(prefill ?? {})) {
      const column = table.columns.find((c) => c.name === name);
      if (column) start[name] = { text: toText(column.kind, value), isNull: value === null, useDefault: false };
    }
    return start;
  });
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const save = useAdminMutation((values: Record<string, unknown>) =>
    rowKey ? api.admin.updateRow(table.name, rowKey, values) : api.admin.createRow(table.name, values),
  );

  const set = (name: string, patch: Partial<FieldState>) =>
    setFields((current) => ({ ...current, [name]: { ...current[name]!, ...patch } }));

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    let values: Record<string, unknown>;
    try {
      values = buildValues(table.columns, fields, original);
    } catch (err) {
      if (err instanceof FieldError) {
        setError(err.message);
        document.getElementById(`field-${err.column}`)?.focus();
        return;
      }
      throw err;
    }
    if (original && Object.keys(values).length === 0) {
      onClose();
      return;
    }
    save.mutate(values, {
      onSuccess: () => {
        toast(original ? "Ligne modifiée." : "Ligne ajoutée.");
        onClose();
      },
      onError: (err) => setError(err.message),
    });
  }

  return (
    <form className="row-form" onSubmit={submit} noValidate>
      <div className="row-form__fields">
        {table.columns.map((column) => (
          <Field
            key={column.name}
            column={column}
            state={fields[column.name]!}
            creating={original === null}
            onChange={(patch) => set(column.name, patch)}
          />
        ))}
      </div>
      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}
      <div className="actions">
        <button type="button" className="button button--quiet" onClick={onClose}>
          Annuler
        </button>
        <button type="submit" className="button" disabled={save.isPending}>
          {original ? "Enregistrer" : "Ajouter"}
        </button>
      </div>
    </form>
  );
}

function Field({
  column,
  state,
  creating,
  onChange,
}: {
  column: AdminColumn;
  state: FieldState;
  creating: boolean;
  onChange: (patch: Partial<FieldState>) => void;
}) {
  const id = `field-${column.name}`;
  const disabled = state.isNull || (creating && state.useDefault);
  const hint = [
    column.kind,
    column.primary_key && "clé primaire",
    column.foreign_key && `→ ${column.foreign_key}`,
    column.max_length && `${column.max_length} car. max`,
  ]
    .filter(Boolean)
    .join(" · ");

  let input;
  if (column.kind === "boolean") {
    input = (
      <select id={id} className="input select" disabled={disabled} value={state.text} onChange={(e) => onChange({ text: e.target.value })}>
        <option value="true">oui (true)</option>
        <option value="false">non (false)</option>
      </select>
    );
  } else if (column.kind === "json" || column.kind === "text") {
    input = (
      <textarea
        id={id}
        className={`input row-form__textarea${column.kind === "json" ? " row-form__textarea--code" : ""}`}
        disabled={disabled}
        rows={column.kind === "json" ? 6 : 3}
        spellCheck={column.kind !== "json"}
        value={state.text}
        onChange={(e) => onChange({ text: e.target.value })}
      />
    );
  } else {
    input = (
      <div className="row-form__inline">
        <input
          id={id}
          className="input"
          type={column.kind === "date" ? "date" : "text"}
          inputMode={column.kind === "integer" ? "numeric" : column.kind === "number" ? "decimal" : undefined}
          maxLength={column.max_length ?? undefined}
          placeholder={column.kind === "datetime" ? "2026-09-21T08:30:00+00:00" : undefined}
          disabled={disabled}
          value={state.text}
          onChange={(e) => onChange({ text: e.target.value })}
        />
        {column.kind === "datetime" && (
          <button
            type="button"
            className="button button--quiet button--small"
            disabled={disabled}
            onClick={() => onChange({ text: new Date().toISOString() })}
          >
            Maintenant
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="field row-form__field">
      <label className="field__label" htmlFor={id}>
        {column.name}
        {!column.nullable && !column.has_default && <span aria-hidden="true"> *</span>}
      </label>
      <span className="field__hint">{hint}</span>
      {input}
      <div className="row-form__toggles">
        {creating && column.has_default && (
          <label className="checkbox">
            <input
              type="checkbox"
              checked={state.useDefault}
              onChange={(e) => onChange({ useDefault: e.target.checked })}
            />
            Valeur par défaut
          </label>
        )}
        {column.nullable && !(creating && state.useDefault) && (
          <label className="checkbox">
            <input type="checkbox" checked={state.isNull} onChange={(e) => onChange({ isNull: e.target.checked })} />
            NULL
          </label>
        )}
      </div>
    </div>
  );
}
